import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, PanelLeft, Sidebar } from 'lucide-react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { activeNavItem, readNavOrigin, type NavItem } from '@/shared/constants/navigation';
import { NavGlyph } from '@/app/NavGlyph';
import { UserMenu } from '@/app/UserMenu';
import { useNavIndicator } from '@/app/useNavIndicator';
import { BrandMark } from '@/shared/components/BrandMark';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { cn } from '@/shared/lib/utils';

/**
 * Três modos, e o padrão mudou (spec-calendar-ux.md §10, decisão 99).
 *
 * O pedido veio do calendário — uma grade de sete colunas com célula de tamanho
 * fixo agradece 180px a mais —, mas **isto é preferência global do painel, não
 * da rota**, e é justamente por isso que resolve: o calendário ganha a largura
 * sem nenhuma rota discutir com a preferência de ninguém.
 */
type SidebarMode = 'expanded' | 'collapsed' | 'hover';

const STORAGE_KEY = 'ibft:sidebar-mode';
/** A chave booleana anterior. Lida uma vez, para converter quem já escolheu. */
const LEGACY_KEY = 'ibft:sidebar-collapsed';

/**
 * A histerese, e ela é a diferença entre "expande" e "atrapalha".
 *
 * Sem atraso para abrir, atravessar a tela com o mouse escancara o menu; sem
 * atraso (maior) para fechar, um desvio de 3px no caminho até um item o fecha na
 * cara de quem ia clicar. Assimétrico de propósito: entrar é uma intenção que se
 * confirma, sair é uma intenção que se desfaz.
 */
const OPEN_DELAY_MS = 150;
const CLOSE_DELAY_MS = 300;

const MODE_LABELS: Record<SidebarMode, string> = {
  hover: 'Expandir ao passar o mouse',
  expanded: 'Sempre expandida',
  collapsed: 'Sempre recolhida',
};

const MODE_ORDER: readonly SidebarMode[] = ['hover', 'expanded', 'collapsed'];

interface AppSidebarProps {
  fullName: string;
  roleLabel: string;
  /**
   * A navegação **já montada** — estáticos recortados por papel mais os itens
   * de vínculo (`AppLayout`).
   *
   * Antes esta prop era `roles` e a coluna chamava `visibleNavItems` sozinha,
   * enquanto o `AppLayout` chamava outra vez para a fileira do celular. Com
   * metade da lista vindo de uma consulta, duas montagens independentes seriam
   * duas navegações capazes de discordar sobre onde a pessoa está — que é
   * exatamente o que os comentários dos dois arquivos dizem que não pode
   * acontecer.
   */
  items: readonly NavItem[];
  /**
   * Contagem da fila de análise (ADR-012 regra 2: fila sem contador não é
   * item). `undefined` enquanto a consulta carrega — o item aparece sem
   * número em vez de com um "0" que ainda não é verdade.
   */
  reviewQueueCount?: number;
  onSignOut: () => void;
}

/** Agrupa os itens visíveis em "topo" (sem seção) e por seção, na ordem de `NAV_ITEMS`. */
function groupBySection(items: readonly NavItem[]) {
  const top: NavItem[] = [];
  const sections: { name: string; items: NavItem[] }[] = [];

  for (const item of items) {
    if (!item.section) {
      top.push(item);
      continue;
    }
    let section = sections.find((entry) => entry.name === item.section);
    if (!section) {
      section = { name: item.section, items: [] };
      sections.push(section);
    }
    section.items.push(item);
  }

  return { top, sections };
}

interface RenderNavItemOptions {
  /** Já resolvido por `activeNavItem` — inclusive a aba de origem (decisão 16). */
  activeItem: NavItem | null;
  collapsed: boolean;
  countOf: (item: NavItem) => number | undefined;
}

function renderNavItem(item: NavItem, { activeItem, collapsed, countOf }: RenderNavItemOptions) {
  const isActive = activeItem?.to === item.to;
  const count = countOf(item);

  return (
    <Link
      key={item.to}
      to={item.to}
      aria-current={isActive ? 'page' : undefined}
      title={collapsed ? `${item.label} — ${item.description}` : item.description}
      className={cn(
        // `relative`: o link precisa pintar ACIMA da marca deslizante, que é
        // irmã dele e está posicionada em absoluto.
        'group relative flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium',
        'transition-[color,background-color,transform] duration-fast ease-out active:scale-[0.98]',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        collapsed && 'justify-center px-0',
        // O fundo do item ativo agora é a marca deslizante — o que sobra aqui
        // é só a cor do texto, que troca em 110ms enquanto a marca viaja em
        // 180ms.
        isActive ? 'text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      <NavGlyph
        icon={item.icon}
        className="transition-transform duration-fast ease-out group-hover:scale-110"
      />
      {!collapsed ? (
        <span className="animate-fade-in flex min-w-0 flex-1 items-center justify-between gap-2 [animation-delay:120ms]">
          <span className="truncate">{item.label}</span>
          {/* ADR-012 regra 2: fila SEMPRE tem contador. `undefined` enquanto a
              consulta carrega — nada de "0" que ainda não é verdade. */}
          {count !== undefined ? (
            <span
              className={cn(
                'shrink-0 rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums',
                count > 0 ? 'bg-primary/10 text-primary' : 'text-muted-foreground/60',
              )}
            >
              {count}
            </span>
          ) : null}
        </span>
      ) : null}
      {collapsed ? (
        <span className="sr-only">
          {item.label}
          {count !== undefined ? ` (${count})` : ''}
        </span>
      ) : null}
    </Link>
  );
}

/**
 * Preferência de interface, não dado do usuário — por isso `localStorage` é o
 * lugar certo aqui, e não contradiz a regra 7 (rascunho vive no servidor).
 * Rascunho precisa atravessar dispositivos; sidebar encolhida, não: no celular
 * ela nem existe.
 *
 * **Converte a chave antiga em vez de descartá-la** (§10): quem escolheu
 * "recolhida" há duas semanas não deve reencontrar a coluna aberta — uma
 * preferência que o sistema esquece sozinho ensina a não configurar nada. Quem
 * nunca escolheu cai no padrão NOVO, que é o `hover`.
 *
 * Leitura preguiçosa no `useState` para não piscar aberta-depois-fechada a cada
 * carga.
 */
function readMode(): SidebarMode {
  if (typeof window === 'undefined') return 'hover';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'expanded' || raw === 'collapsed' || raw === 'hover') return raw;

    const legacy = window.localStorage.getItem(LEGACY_KEY);
    if (legacy === 'true') return 'collapsed';
    if (legacy === 'false') return 'expanded';
    return 'hover';
  } catch {
    // Modo privado do Safari e afins: `localStorage` existe e lança ao ser
    // lido. Preferência perdida é aceitável; tela em branco não é.
    return 'hover';
  }
}

export function AppSidebar({
  fullName,
  roleLabel,
  items,
  reviewQueueCount,
  onSignOut,
}: AppSidebarProps) {
  const [mode, setMode] = useState<SidebarMode>(readMode);
  /** No modo `hover`: a coluna está aberta AGORA, por mouse ou por teclado. */
  const [peeking, setPeeking] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname, state } = useLocation();
  const timerRef = useRef<number | null>(null);

  /**
   * **Em toque não há hover**, e sem esta checagem um iPad receberia o
   * `pointerenter` sintético do primeiro toque: a coluna abriria sozinha em cima
   * do conteúdo que a pessoa tentou tocar.
   *
   * ⚠️ Sem o botão do cabeçalho (§10.2), num aparelho de toque com 768px ou mais
   * o modo `hover` fica **permanentemente estreito** — a saída é o menu do
   * rodapé, escolhendo "Sempre expandida". Abaixo de 768px a coluna nem existe
   * (`hidden md:flex`), então o caso é o tablet, não o celular.
   */
  const canHover = useMediaQuery('(hover: hover)');

  const isExpanded = mode === 'expanded' || (mode === 'hover' && peeking);
  /**
   * **Sobrepõe, não empurra** (§10, detalhe 1). Se empurrasse, o calendário
   * refluiria a cada passada de mouse — e uma grade que muda de largura sozinha
   * é a definição de tela instável. Nos outros dois modos a largura é estável e
   * não há nada para sobrepor.
   */
  const isOverlaying = mode === 'hover' && peeking;
  const railWidth = mode === 'expanded' ? '248px' : '68px';

  const { top, sections } = useMemo(() => groupBySection(items), [items]);
  // A aba acesa é a de ORIGEM quando houve origem (decisão 16) — abrir um
  // projeto pela fila mantém "Análise" acesa, e o F5 devolve o canônico.
  const activeItem = activeNavItem(items, pathname, readNavOrigin(state));
  // Recolher a coluna muda a altura e a largura dos itens: a marca precisa ser
  // remedida, ou fica com o tamanho da coluna aberta dentro do rail de 68px.
  const indicator = useNavIndicator<HTMLElement>('vertical', isExpanded);

  const countOf = useCallback(
    (item: NavItem): number | undefined =>
      item.to === '/analise' ? reviewQueueCount : undefined,
    [reviewQueueCount],
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Sem `localStorage` a escolha vale só nesta visita, e isso não é erro que
      // se conta a um voluntário (RNF-U03).
    }
  }, [mode]);

  // Trocar de modo cancela qualquer espiada em curso: sair de `hover` com a
  // coluna aberta a deixaria sobreposta para sempre.
  useEffect(() => {
    if (mode !== 'hover') setPeeking(false);
  }, [mode]);

  const schedule = useCallback((open: boolean, delay: number) => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setPeeking(open), delay);
  }, []);

  useEffect(() => () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
  }, []);

  return (
    // O invólucro segura a largura de REPOUSO; a coluna flutua dentro dele. É
    // isto que faz o modo `hover` sobrepor em vez de empurrar.
    <div
      style={{ width: railWidth }}
      className="relative h-full transition-[width] duration-slow ease-out"
      onPointerEnter={
        mode === 'hover' && canHover ? () => schedule(true, OPEN_DELAY_MS) : undefined
      }
      onPointerLeave={
        mode === 'hover' && canHover ? () => schedule(false, CLOSE_DELAY_MS) : undefined
      }
      /**
       * **Foco por teclado expande igual ao mouse** (§10, detalhe 3), e sem
       * atraso: quem chegou com `Tab` já declarou a intenção. `Esc` recolhe.
       * Sem isto, navegar por teclado no modo padrão significaria tabular por
       * cinco ícones sem rótulo visível.
       */
      onFocusCapture={mode === 'hover' ? () => setPeeking(true) : undefined}
      onBlurCapture={
        mode === 'hover'
          ? (event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                setPeeking(false);
              }
            }
          : undefined
      }
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          setPeeking(false);
          setMenuOpen(false);
        }
      }}
    >
      <aside
        className={cn(
          'absolute inset-y-0 left-0 z-40 flex h-full flex-col border-r border-border bg-background',
          // `prefers-reduced-motion` global (styles/index.css) já zera esta
          // transição — quem desligou o movimento recebe a troca direta de
          // largura, que é o comportamento pedido no detalhe 4.
          'transition-[width,box-shadow] duration-slow ease-out',
          isExpanded ? 'w-[248px]' : 'w-[68px]',
          // A sombra só existe quando ela está POR CIMA de alguma coisa: é o
          // que diz "isto é uma camada flutuante, o conteúdo continua ali
          // atrás". Nos modos fixos a coluna é parte da moldura, e uma sombra
          // permanente a faria descolar da página.
          isOverlaying && 'shadow-[0_0_0_1px_hsl(var(--border)),8px_0_32px_-12px_hsl(222_24%_12%/0.25)]',
        )}
      >
        {/*
          **Altura fixa de 56px nos dois estados, e uma linha só.**

          Enquanto o botão de recolher morava aqui, o modo estreito não tinha
          como pôr os dois lado a lado e empilhava (`flex-col`) — então abrir
          por hover não era só a coluna alargando: era o cabeçalho trocando de
          eixo no meio do movimento, com o botão SALTANDO de baixo da marca
          para a direita dela. Sem o botão não há o que empilhar, a marca fica
          parada e o que se move é só a largura.

          `justify-center` no estreito porque a marca sozinha em 68px com
          `px-3` sobra 12px de um lado — centrada ela fica no eixo dos ícones
          de navegação, que é onde o olho a procura.
        */}
        <div
          className={cn(
            'flex h-14 items-center gap-2.5 px-3',
            !isExpanded && 'justify-center',
          )}
        >
          <NavLink
            to="/"
            className="group flex min-w-0 items-center gap-2.5 rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            title="Gestão IBFT"
          >
            {/* `group-active` e não `active`: pressionar o NOME também tem de
                recuar a marca. */}
            <BrandMark className="size-8 shrink-0 transition-transform duration-fast ease-out group-active:scale-95" />
            {/* O texto entra em fade DEPOIS que a coluna terminou de abrir: sem
                o atraso ele aparece já na primeira fração do movimento,
                espremido contra a marca, e o que se vê é o nome sendo esmagado. */}
            {isExpanded ? (
              <span className="animate-fade-in truncate text-sm font-semibold tracking-tight [animation-delay:120ms]">
                Gestão IBFT
              </span>
            ) : (
              /* A marca é `aria-hidden`. Sem esta linha, a coluna recolhida
                 deixaria o link para o início SEM NOME ACESSÍVEL. */
              <span className="sr-only">Gestão IBFT</span>
            )}
          </NavLink>

        </div>

        <nav
          ref={indicator.containerRef}
          className="relative flex-1 space-y-1 overflow-y-auto px-3 py-2"
          aria-label="Navegação principal"
        >
          {/* A marca do item ativo, deslizando. `left-3 right-3` a fazem
              acompanhar o `px-3` do `nav`, inclusive quando a coluna encolhe
              para 68px — por isso `isExpanded` é a chave de recálculo do hook. */}
          {indicator.style ? (
            <span
              aria-hidden="true"
              style={indicator.style}
              className="absolute left-3 right-3 rounded-md bg-primary/10 transition-[top,height] duration-base ease-out"
            />
          ) : null}

          {/* `Link` e não `NavLink`: quem decide o item ativo é `isNavItemActive`,
              porque o casamento por prefixo do `NavLink` não sabe que
              `/ministerios/:id` pertence à aba Coordenações. */}
          {top.map((item) => renderNavItem(item, { activeItem, collapsed: !isExpanded, countOf }))}

          {/* Seções de fila (ADR-012 regra 4): separador + rótulo em maiúsculas.
              Some no modo recolhido — não cabe, e o ícone sozinho já é o item. */}
          {sections.map((section) => (
            <div key={section.name} className={cn(isExpanded && 'pt-3')}>
              {isExpanded ? (
                <div className="mb-1 mt-2 border-t border-border px-2.5 pt-3 text-[0.6875rem] font-medium uppercase tracking-wider text-muted-foreground/80">
                  {section.name}
                </div>
              ) : (
                <div className="my-1.5 border-t border-border" aria-hidden="true" />
              )}
              {section.items.map((item) =>
                renderNavItem(item, { activeItem, collapsed: !isExpanded, countOf }),
              )}
            </div>
          ))}
        </nav>

        <div className="space-y-1 border-t border-border p-2">
          <UserMenu
            fullName={fullName}
            roleLabel={roleLabel}
            collapsed={!isExpanded}
            onSignOut={onSignOut}
          />

          {/*
            **O menu de modo mora no RODAPÉ da própria barra** (§10): é uma
            preferência sobre a barra, e o lugar de um controle é junto do que
            ele muda (mapeamento). Numa tela de Configurações — que este painel
            não tem — ela viraria um item que ninguém encontra.
          */}
          <ModeMenu
            mode={mode}
            onChange={setMode}
            open={menuOpen}
            onOpenChange={setMenuOpen}
            collapsed={!isExpanded}
          />
        </div>
      </aside>
    </div>
  );
}

/**
 * Os três modos, num menu que abre para CIMA — ele vive no rodapé, e um menu que
 * abrisse para baixo sairia da tela.
 *
 * Escrito à mão, como o `DayContextMenu` e a `Sheet`: `Esc`, clique fora e foco
 * são as três coisas que um menu de biblioteca traria, e nenhuma delas justifica
 * uma dependência (regra 13).
 */
function ModeMenu({
  mode,
  onChange,
  open,
  onOpenChange,
  collapsed,
}: {
  mode: SidebarMode;
  onChange: (mode: SidebarMode) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collapsed: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function onPointer(event: MouseEvent) {
      if (!ref.current?.contains(event.target as Node)) onOpenChange(false);
    }
    document.addEventListener('mousedown', onPointer, true);
    return () => document.removeEventListener('mousedown', onPointer, true);
  }, [open, onOpenChange]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Barra lateral: ${MODE_LABELS[mode]}`}
        className={cn(
          'flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-sm text-muted-foreground',
          'transition-[color,background-color] duration-fast ease-out',
          'hover:bg-accent hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
          collapsed && 'justify-center px-0',
        )}
      >
        <Sidebar aria-hidden="true" className="size-[18px] shrink-0" />
        {collapsed ? (
          <span className="sr-only">Barra lateral: {MODE_LABELS[mode]}</span>
        ) : (
          <span className="animate-fade-in truncate text-xs [animation-delay:120ms]">
            {MODE_LABELS[mode]}
          </span>
        )}
      </button>

      {open ? (
        <div
          role="menu"
          aria-label="Comportamento da barra lateral"
          className="absolute bottom-full left-0 z-50 mb-1 w-[15rem] origin-bottom-left animate-menu-in rounded-lg border border-border bg-popover p-1 shadow-lg"
        >
          {MODE_ORDER.map((entry) => (
            <button
              key={entry}
              type="button"
              role="menuitemradio"
              aria-checked={entry === mode}
              onClick={() => {
                onChange(entry);
                onOpenChange(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm',
                'transition-colors duration-fast ease-out hover:bg-accent focus-visible:bg-accent focus-visible:outline-none',
                entry === mode ? 'font-medium text-popover-foreground' : 'text-muted-foreground',
              )}
            >
              <PanelLeft aria-hidden="true" className="size-4 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{MODE_LABELS[entry]}</span>
              {entry === mode ? (
                <Check aria-hidden="true" className="size-4 shrink-0 text-primary" />
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
